import {useSuspenseQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {useState} from 'react';
import {parseDateWithFormat} from '@shared/dateUtils';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {ApiResponseGetTireBoundResponse} from '@di-shared/api/di/__generated';
import {
  convertPressure,
  getFrontTireBound,
  getIsRecommendedTireSingleSpec,
  getPressureUnit,
  getRearTireBound,
  getTirePressureAccuracyByTemperature,
  getTireStatus,
  splitTireName,
} from '@di-shared/features/diag/tire/tirePressureUtil';
import {PressureUnit, TirePressureStatus} from '@di-shared/features/diag/tire/const';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {InvalidOrAbnormal} from '@di-shared/api/di/type';

function useTirePressure(vin: string) {
  const puid = environmentStore.getState().puid;
  const [
    {data: tireRecommendedData, refetch: refetchTireRecommended},
    {data: tireBoundData, refetch: refetchTireBoundData},
    {data: carWarningLightStatusData},
  ] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.tire.getTireRecommended), vin, puid],
        queryFn: () =>
          diApi.tire.getTireRecommended({
            puid,
            vin,
          }),
      },
      //TODO: tire bound api 임
      {
        queryKey: [getQueryKey(diApi.tire.getBatteryStatus1), vin],
        queryFn: () =>
          diApi.tire.getBatteryStatus1({
            vin,
          }),
        select: (data: ApiResponseGetTireBoundResponse) => ({
          ...data.data,
          firstFl: convertPressure(data.data.firstFl, data.data.firstUnit),
          firstFr: convertPressure(data.data.firstFr, data.data.firstUnit),
          firstRl: convertPressure(data.data.firstRl, data.data.firstUnit),
          firstRr: convertPressure(data.data.firstRr, data.data.firstUnit),
          firstUnit: PressureUnit.PSI,
        }),
      },
      {
        queryKey: [getQueryKey(ccWebviewUtils.getCarWarningLightStatus)],
        queryFn: () => ccWebviewUtils.getCarWarningLightStatus(),
      },
    ],
  });
  const {
    tirePressureLampFL,
    tirePressureLampFR,
    tirePressureLampRR,
    tirePressureLampRL,
    firstUnit,
    firstFl,
    firstFr,
    firstRl,
    firstRr,
    tireTime,
    cstaAvg: outSideTemperature,
  } = tireBoundData;
  const {tirePsiList, tires: recommendedTires, registeredTire} = tireRecommendedData.data;

  const [registeredFrontTire, registeredRearTire] = splitTireName(registeredTire);
  const registeredFrontTireBound = getFrontTireBound(registeredFrontTire, tirePsiList);
  const registeredRearTireBound = getRearTireBound(registeredRearTire, tirePsiList);
  /**
   * isRecommendedTireSingleSpec
   * <제공 타이어 스펙이 1개일 경우>
   * <타이어 스펙별 공기압 차이가 없을 경우>
   */
  const isRecommendedTireSingleSpec = getIsRecommendedTireSingleSpec(tirePsiList);

  // const [registeredFrontTireBound, registeredRearTireBound] = getTirePsiFromServerFormat(registeredTire, tirePsiList);
  /**
   * 등록된 타이어
   * isSameTire: 앞뒤가 똑같은 타이어인지?
   * 앞뒤가 다르면 245/40R20(앞) 275/35R20(뒤) 표기
   * isSingleSpec:
   */
  const isFrontRearSameTire = registeredFrontTire === registeredRearTire;

  const [selectedTire, setSelectedTire] = useState(registeredTire);

  function changeSelectedTire(selectedTire: string) {
    setSelectedTire(selectedTire);
  }

  /**
   * 외기온도 case
   * 기준온도 19도 미만
   * 기준온도 19~21 도
   * 기준온도 22도 이상
   * 999도 리턴 시
   * nodata/etc
   */
  const tirePressureAccuracy = getTirePressureAccuracyByTemperature(outSideTemperature);

  const flTireStatus = getTireStatus(firstFl, registeredFrontTireBound, tirePressureLampFL);
  const frTireStatus = getTireStatus(firstFr, registeredFrontTireBound, tirePressureLampFR);
  const rlTireStatus = getTireStatus(firstRl, registeredRearTireBound, tirePressureLampRL);
  const rrTireStatus = getTireStatus(firstRr, registeredRearTireBound, tirePressureLampRR);

  const isWarningLightOn =
    carWarningLightStatusData?.tire?.frontLeft === InvalidOrAbnormal.Abnormal ||
    carWarningLightStatusData?.tire?.frontRight === InvalidOrAbnormal.Abnormal ||
    carWarningLightStatusData?.tire?.rearLeft === InvalidOrAbnormal.Abnormal ||
    carWarningLightStatusData?.tire?.rearRight === InvalidOrAbnormal.Abnormal;

  const isLowerPressure = [flTireStatus, frTireStatus, rrTireStatus, rlTireStatus].some(
    (tireStatus) => tireStatus === TirePressureStatus.LOWER
  );

  const isInspectionRequired = isLowerPressure || isWarningLightOn;

  const pressureUnit = getPressureUnit(firstUnit);

  return {
    myTire: {
      fl: {pressure: firstFl, status: flTireStatus},
      fr: {pressure: firstFr, status: frTireStatus},
      rl: {pressure: firstRl, status: rlTireStatus},
      rr: {pressure: firstRr, status: rrTireStatus},
    },
    updatedTime: parseDateWithFormat(tireTime),
    registeredTire,
    isFrontRearSameTire,
    registeredFrontTire,
    registeredRearTire,
    registeredFrontTireBound,
    registeredRearTireBound,
    recommendedTires,
    outSideTemperature,
    tirePressureAccuracy,
    selectedTire,
    changeSelectedTire,
    pressureUnit,
    isRecommendedTireSingleSpec,
    isInspectionRequired,
    refetchTireRecommended,
    refetchTireBoundData,
    /**
     * 등록된 타이어 없을 때 -> '타이어 선택하기' 버튼 보여줌
     * 등록된 타이어 있을 때 && single spec 일때 -> 안보여줌
     * 등록된 타이어 있을 때 && multi spec 일때 -> 보여줌
     */
    canChangeTireSpec: !(registeredTire && isRecommendedTireSingleSpec),
  };
}

export default useTirePressure;
